import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { DataTable } from "@life-line-lab/components-react";
import { ExpandableConfig } from "antd/lib/table/interface";
import { useQuery, useSession } from "Hooks/index";
import React, {
  useState,
  createContext,
  useContext,
  FC,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { GlobalFilterAtom } from "Recoil/global_filter.atom";
import { tabStatus } from "Data/order";
import { Option } from "Types/common.types";

interface OrderListingProviderProps {
  children: ReactNode
}

interface FilterObjProps { 
  orgID?: any[]
  orderStatus?: string[]
  ancestorOrgIDs?: string[]
  customersList?: string
  deliveryType?: string
  locationID?: string
  deliveryDate?: string
}

interface OrderListingStatesProps {
  allOrderList: any[]
  setAllOrderList: React.Dispatch<React.SetStateAction<any[]>>;
  currentListCount: number
  setCurrentListCount: React.Dispatch<React.SetStateAction<number>>;
  customersList: any[]
  setCustomersList: React.Dispatch<React.SetStateAction<any[]>>;
  locationList: any[]
  setLocationList: React.Dispatch<React.SetStateAction<any[]>>;
  dropOffLocationList: any[]
  setDropOffLocationList: React.Dispatch<React.SetStateAction<any[]>>;
  workingHours: any
  setWorkingHours: React.Dispatch<React.SetStateAction<any>>;
  selectedRows: any[]
  setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>;
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  filterObj: FilterObjProps
  setFilterObj: React.Dispatch<React.SetStateAction<FilterObjProps>>;
  expandable: ExpandableConfig<any>;
  initialFilters: FilterObjProps;
  globalFilterState: any;
}

const OrderListingContext = createContext<OrderListingStatesProps | undefined>(
  undefined,
);

export const OrderListingProvider: FC<OrderListingProviderProps> = ({ children }) => {

  const { org } = useSession();
  const { t } = useTranslation();
  const query = useQuery();
  const tabKey = query.get("tabKey") || "0";
  const globalFilterState = useRecoilValue(GlobalFilterAtom);
  
  const initialFilters: FilterObjProps = {
    orgID: globalFilterState?.selectedOrgIDs?.length ? undefined : [org._id],
    orderStatus: tabStatus[tabKey],
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : undefined,
    customersList: "",
    deliveryType: "",
    locationID: "",
    deliveryDate: "",
  };

  const [allOrderList, setAllOrderList] = useState<any>([]);
  const [currentListCount, setCurrentListCount] = useState(0);
  const [customersList, setCustomersList] = useState<Option[]>([]);
  const [locationList, setLocationList] = useState<Option[]>([]);
  const [dropOffLocationList, setDropOffLocationList] = useState<Option[]>([]);
  const [workingHours, setWorkingHours] = useState({});
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterObj, setFilterObj] = useState<any>(initialFilters);

  const splitShipmentColumns: any[] = [
    {
      title: t("label.trackingNo").toLocaleUpperCase(),
      dataIndex: "trackingNumber",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.volume").toLocaleUpperCase(),
      dataIndex: ["dimension"],
      width: 70,
      render: (dimension: any) => {
        return (
          <span>
            {dimension?.length} x {dimension?.width} x {dimension?.height}
          </span>
        );
      },
    },
    {
      title: t("label.weight").toLocaleUpperCase(),
      dataIndex: ["weight"],
      width: 70,
      render: (weight: any) => {
        return <span>{weight || <span>&#11835;</span>}</span>;
      },
    },
    {
      title: t("label.type").toLocaleUpperCase(),
      dataIndex: ["parcels"],
      width: 80,
      render: (parcels: any[]) => {
        return (
          <span>
            {parcels.map((parcel: any) => {
              return (
                <span>
                  {parcel?.name || <span>&#11835;</span>}
                  <br />
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      title: t("label.value").toLocaleUpperCase(),
      dataIndex: ["totalValueOfGoods"],
      width: 70,
      render: (totalValueOfGoods: any) => {
        return (
          <span>
            {parseInt(totalValueOfGoods).toLocaleString() || (
              <span>&#11835;</span>
            )}
            <br />
          </span>
        );
      },
    },
  ];

  const expandable: ExpandableConfig<any> = {
    rowExpandable: (record: any) => record.childrenOrderIDs,
    expandIcon: ({ expanded, onExpand, record }) => {
      if (record.childrenOrderIDs) {
        return expanded ? (
          <CaretUpOutlined onClick={(e) => onExpand(record, e)} />
        ) : (
          <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
        );
      }
    },
    expandedRowRender: (record: any) => {
      return (
        <div className="">
          <DataTable
            width={50}
            columns={splitShipmentColumns}
            dataSource={record.childOrders || []}
          />
        </div>
      );
    },
  };

  return (
    <OrderListingContext.Provider
      value={{
        allOrderList,
        setAllOrderList,
        currentListCount,
        setCurrentListCount,
        customersList,
        setCustomersList,
        locationList,
        setLocationList,
        dropOffLocationList,
        setDropOffLocationList,
        workingHours,
        setWorkingHours,
        selectedRows,
        setSelectedRows,
        isLoading,
        setIsLoading,
        filterObj,
        setFilterObj,
        expandable,
        initialFilters,
        globalFilterState
      }}
    >
      {children}
    </OrderListingContext.Provider>
  );
}

export const useOrderListingStates = () => {
  const context = useContext(OrderListingContext);

  if (context === undefined) {
    throw new Error("OrderListingProvider not found.");
  }
  return context;
};
